import { series as asyncSeries } from "async";
import { BaseService } from "../baseService";
import { RouterClient } from "../../../clients/routerClient";
import DockingMain from "./Docking/dockingMain";
import { Launcher, activeWindows } from "./Launcher/launcher";
import { ServiceEntryPoints as SetupAllEntryPoints } from "./ServiceEntryPoints/setupAllEntryPoints";
import { ConfigClient } from "../../../clients/configClient";
import { Logger } from "../../../clients/logger";
import { System } from "../../../common/system";
import { getMonitorsInstance } from "../../../common/Monitors";
import { getStore } from "./store";
import { actions } from "./modules/actions";
import { FEAGlobals } from "../../../common";
import { PhysicalWindowStateEvent } from "../../../FEA";

Logger.start();

const store = getStore();

export class WindowService extends BaseService {
	launcher: Launcher | null = null;

	shuttingDown: boolean = false;

	params: any;

	constructor(params) {
		params.name = "windowService";
		super(params);
		this.params = params;
		this.doShutdown = this.doShutdown.bind(this);
		this.onShutdown(this.doShutdown);
		/**
		 * When a window executes `window.open`, FEA will capture that event, modify
		 * some of the params, create a BrowserWindow, and bubble up this event.
		 * Windows created in this way can have their features restricted if desired.
		 * addExternallyOpenedWindow makes sure they can only do what they're supposed to. It
		 * also makes sure they're brought into the Finsemble fold.
		 */
		System.addEventListener("new-window-created", (response: any) => {
			this.addExternallyOpenedWindow(response);
		});
		/**
		 * When native apps are launched from outside Finsemble, they will register themselves for wraps
		 */
		RouterClient.addResponder("Finsemble.NewNativeWindow", async (err, response) => {
			await this.launcher?.addNewNativeWindow(response?.data ?? {});
			response?.sendQueryResponse(null, null);
		});
	}

	processWindowEventStream() {
		const processEvent = (data: PhysicalWindowStateEvent) => {
			// Store dispatches can slow down Finsemble shutdown
			if (this.shuttingDown) return;
			store.dispatch(actions.windowEvent(data));
		};
		// Subscribe to FEA's WindowEventStream
		FEAGlobals.fin.desktop.System.subscribeToWindowEventStream(processEvent);

		RouterClient.addListener<PhysicalWindowStateEvent>("assimilation.WindowEventStream", (err, response) => {
			processEvent(response.data);
		});
	}

	async initializeWindowService(manifest: any) {
		Logger.system.debug("WindowService.initialize start");
		this.processWindowEventStream();

		const monitorsInstance = await getMonitorsInstance();
		const dockingMain = new DockingMain(this.params, monitorsInstance, store);

		this.launcher = new Launcher(manifest, monitorsInstance, dockingMain, store);

		// This test rig is exposed on the global for Finsemble's suite of Selenium e2e tests
		// to examine and manipulate window service internals.
		(global as any).windowServiceTestRig = {
			dockingMain,
			activeWindows,
		};

		// setup external interfaces (i.e. the API supported by the window service)
		const setupAllEntryPoints = new SetupAllEntryPoints(dockingMain, this.launcher);

		await dockingMain.initialize();
		const result = await ConfigClient.get(["finsemble-electron-adapter", "useDOMBasedMovement"]);
		if (result.data === false) {
			store.dispatch(actions.disableDOMBasedMovement());
		}

		await this.launcher.initialize();
		await setupAllEntryPoints.initialize();
	}

	/**
	 * Pass through to the launcher's appropriate window function.
	 * This sits at the top level of the windowService so that we can
	 * do things before/after window appropriation if necessary in the future.
	 */
	async addExternallyOpenedWindow({
		appUUID,
		name,
		windowType = "WebWindow",
		options,
		appName,
	}: {
		appUUID: string;
		name: string;
		windowType: string;
		options: {};
		appName?: string;
	}) {
		await this.launcher?.addExternallyOpenedWindow({ appUUID, name, windowType, options, appName });
	}

	// This method handles the shutdown sequence for the launcherService. This currently doesn't finish preventing the service from shutting down
	doShutdown() {
		return new Promise((resolve) => {
			this.shuttingDown = true;
			asyncSeries([
				(done) => {
					this.launcher?.shutdown(done);
				},
				resolve,
			]);
		});
	}
}

const serviceInstance = new WindowService({
	name: "windowService",
	startupDependencies: {},
	shutdownDependencies: {
		services: ["workspaceService"],
	},
});

serviceInstance.onBaseServiceReady((callback) => {
	ConfigClient.getMultiple([], async (err, manifest) => {
		await serviceInstance.initializeWindowService(manifest);
		callback();
	});
});

serviceInstance.start();
